import { useCallback, useState } from 'react';
import { logger } from '@fiverr-private/obs';
import { raiseToast } from '@fiverr-private/toast';
import { getContext } from '@fiverr-private/fiverr_context';
import { resendEmailWithCode } from '../../../api';
import { isAxiosError } from '../../../utils/isAxiosError';
import { translateBackendError } from '../../../utils/translateBackendError';
import { ResendEmailWithCodeResponse, ResendEmailWithCodeResult } from '../../../api/resendEmailWithCode';
import { appTranslate } from '../../../i18n';
import { useResendCooldownContext } from '../../shared/ResendCooldownContext';

const PACKAGE_NAME = '@fiverr-private/code_activation';

export const useResendWithCooldown = () => {
    const { isUnavailable, startCooldown, commitCooldown, cancelCooldown } = useResendCooldownContext();
    const [isResending, setIsResending] = useState(false);

    const resend = useCallback(
        async (email: string, source?: string) => {
            if (!startCooldown()) {
                return undefined;
            }

            let result: ResendEmailWithCodeResult | undefined;

            setIsResending(true);
            try {
                result = await resendEmailWithCode(email);

                if (result.success) {
                    commitCooldown();
                } else {
                    cancelCooldown();
                }

                if (result.translatedError) {
                    raiseToast({ message: result.translatedError, type: 'error', location: 'top-end' });
                }
            } catch (error) {
                cancelCooldown();
                result = { success: false };

                if (isAxiosError(error)) {
                    const errorMessage = (error.response?.data as ResendEmailWithCodeResponse | undefined)?.error;
                    logger.error(new Error(`Resend Activation Email Error: ${errorMessage}`), {
                        package: PACKAGE_NAME,
                        source,
                        userId: getContext().userId,
                        error: {
                            status: error.response?.status,
                            url: error.config?.url,
                            method: error.config?.method,
                            code: error.code,
                            responseData: error.response?.data,
                            // new Error(message) above gives a stack rooted at this catch block; keep the
                            // original axios error's stack too so the real call path isn't lost.
                            originalStack: error.stack,
                        },
                    });
                    result.translatedError = translateBackendError(errorMessage);
                } else if (error instanceof Error) {
                    logger.error(error, {
                        message: `Resend Activation Email Error: ${error.message}`,
                        package: PACKAGE_NAME,
                        source,
                        userId: getContext().userId,
                    });
                    result.translatedError = appTranslate<string>('errors.general.unspecified');
                }

                if (result.translatedError) {
                    raiseToast({ message: result.translatedError, type: 'error', location: 'top-end' });
                }
            }
            setIsResending(false);

            return result;
        },
        [startCooldown, commitCooldown, cancelCooldown]
    );

    return {
        isUnavailable,
        isResending,
        resend,
    };
};
